import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Container,
  Divider,
  OutlinedInput,
  IconButton,
  Tooltip,
  FormControl,
  InputAdornment,
  Button,
  FormHelperText
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import Logo from 'src/components/LogoSign';

import { styled } from '@mui/material/styles';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import InstagramIcon from '@mui/icons-material/Instagram';
import MailTwoToneIcon from '@mui/icons-material/MailTwoTone';
import { add } from 'date-fns';
import TopBarTitleWrapper from 'src/components/TopBarTitleWrapper';
import TopBarContent from 'src/content/applications/Messenger/TopBarContent';

const MainContent = styled(Box)(
  () => `
    height: 100%;
    display: flex;
    flex: 1;
    overflow: auto;
    flex-direction: column;
    align-items: center;
    justify-content: center;
`
);

const TypographyH1 = styled(Typography)(
  ({ theme }) => `
  font-size: ${theme.typography.pxToRem(75)};
`
);

// set padding left & right to 10 and font size to 24px when in small devices for TypographyH1
TypographyH1.defaultProps = {
  sx: {
    '@media (max-width: 600px)': {
      fontSize: '24px',
      px: 0,
    }
  }
};


const TypographyH3 = styled(Typography)(
  ({ theme }) => `
  color: ${theme.colors.alpha.black[50]};
`
);

const OutlinedInputWrapper = styled(OutlinedInput)(
  ({ theme }) => `
    background-color: ${theme.colors.alpha.white[100]};
`
);

const ButtonNotify = styled(Button)(
  ({ theme }) => `
    margin-right: -${theme.spacing(1)};
`
);

interface ComingSoonComponentProps {
  header?: string;
  title?: string;
  subtitle?: string;
  launchDate?: string;
  imageSrc?: string;
  imageHeight?: number;
  showTimer?: boolean;
  showEmailForm?: boolean;
  showSocialLinks?: boolean;
  onEmailSubmit?: (email: string) => void;
}

function ComingSoonComponent({
  header = "Coming Soon",
  title = "Coming Soon",
  subtitle = "We're working on implementing the last features before our launch!",
  launchDate = "2025-04-15T12:30:00",
  imageSrc = "/static/images/status/coming-soon.svg",
  imageHeight = 200,
  showTimer = true,
  showEmailForm = false,
  showSocialLinks = false,
  onEmailSubmit
}: ComingSoonComponentProps) {
  const calculateTimeLeft = () => {
    const difference = +new Date(launchDate) - +new Date();
    let timeLeft = {};

    if (difference > 0) {
      timeLeft = {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / 1000 / 60) % 60),
        seconds: Math.floor((difference / 1000) % 60)
      };
    }

    return timeLeft;
  };

  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());
  const [email, setEmail] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);
    
    return () => clearTimeout(timer);
  }, [launchDate]);

  const timerComponents = [];

  Object.keys(timeLeft).forEach((interval) => {
    if (!timeLeft[interval] && interval in ['days', 'hours']) {
      return;
    }

    timerComponents.push(
      // set px: 1 for small screen devices else 3
      <Box textAlign="center" key={interval} sx={{ px: { xs: 1, md: 3 } }}>
        <TypographyH1 variant="h1">{timeLeft[interval]}</TypographyH1>
        <TypographyH3 variant="h3">{interval}</TypographyH3>
      </Box>
    );
  });

  const handleEmailSubmit = () => {
    if (onEmailSubmit && email) {
      onEmailSubmit(email);
      setEmail('');
    }
  };

  return (
    <>
    <TopBarTitleWrapper>
      <Typography variant="h1" sx={{ mt: 4, mb: 2 }}>{header}</Typography>
    </TopBarTitleWrapper>
    <MainContent>
      <Container maxWidth="md">
        <Box textAlign="center" mb={3} mt={12}>
          <Container maxWidth="sm">
            <Typography variant="h1" sx={{ mt: 4, mb: 2 }}>
              {title}
            </Typography>
            <Typography
              variant="h3"
              color="text.secondary"
              fontWeight="normal"
              sx={{ mb: 4 }}
            >
              {subtitle}
            </Typography>
          </Container>
          <img
            alt="Coming Soon"
            height={imageHeight}
            src={imageSrc}
          />
        </Box>

        {showTimer && (
          <Box display="flex" justifyContent="center">
            {timerComponents.length ? timerComponents : <></>}
          </Box>
        )}

        <Container maxWidth="sm">
          <Box sx={{ textAlign: 'center', p: 4 }}>
            {showEmailForm && (
              <FormControl variant="outlined" fullWidth>
                <OutlinedInputWrapper
                  type="email"
                  placeholder="Enter your email address here..."
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  endAdornment={
                    <InputAdornment position="end">
                      <ButtonNotify variant="contained" size="small" onClick={handleEmailSubmit}>
                        Notify Me
                      </ButtonNotify>
                    </InputAdornment>
                  }
                  startAdornment={
                    <InputAdornment position="start">
                      <MailTwoToneIcon />
                    </InputAdornment>
                  }
                />
                <FormHelperText>
                  We'll email you once our website is launched!
                </FormHelperText>
              </FormControl>
            )}
            
            {showSocialLinks && (
              <>
                <Divider sx={{ my: 4 }} />
                <Box sx={{ textAlign: 'center' }}>
                  <Tooltip arrow placement="top" title="Facebook">
                    <IconButton color="primary">
                      <FacebookIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip arrow placement="top" title="Twitter">
                    <IconButton color="primary">
                      <TwitterIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip arrow placement="top" title="Instagram">
                    <IconButton color="primary">
                      <InstagramIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </>
            )}
          </Box>
        </Container>
      </Container>
    </MainContent>
    </>
  );
}

// Original page component that uses the new ComingSoonComponent
function StatusComingSoon() {
  return (
    <>
      <Helmet>
        <title>Brewry - Coming Soon</title>
      </Helmet>
      <ComingSoonComponent />
    </>
  );
}

export { ComingSoonComponent };
export default StatusComingSoon;
